// START IMPORTS
import {
  PdfUpgradeBrandWrapper,
  PdfModalsState,
} from '@ltvco/refresh-lib/payments';
import { Box, styled, useMediaQuery, useTheme } from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import { useAccount } from '@ltvco/refresh-lib/v1';
import { useBannerLogic } from '@ltvco/refresh-lib/v2';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  getDeepSearchWrapperStyles,
  deepSearchContentStyles,
} from 'components/DeepSearch/appContainerOverrides';
import { useFirstRecordSearch } from 'routes/utils/useFirstRecordSearch';
// END IMPORTS

// START STYLED_COMPONENTS
const AppWrapper = styled('div')<{
  isOnReportView?: boolean;
  isOnChatBvView?: boolean;
  isDesktop?: boolean;
}>(({ theme, isOnReportView, isOnChatBvView, isDesktop }) => ({
  marginTop: isOnReportView
    ? '70px'
    : isOnChatBvView && !isDesktop
    ? '0'
    : '80px',
  backgroundColor: theme.palette.background.default,
  paddingBottom: theme.spacing(0.25),
  [theme.breakpoints.up('md')]: {
    margin: '55px 0 0 0',
  },
}));
// END STYLED_COMPONENTS

export function AppContainer({
  children,
}: {
  children: React.ReactNode | React.ReactNode[];
}) {
  // START HOOKS_AND_QUERIES
  const location = useLocation();
  const theme = useTheme();
  const { isDesktop } = useScreenSize();
  const { isUserInTest } = useBannerLogic();
  const { data: account } = useAccount(true);
  const { showFirstTimePDFModal } = useFirstRecordSearch();
  const isMobileChatBreakpoint = useMediaQuery('(max-width: 1024px)');
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [pdfModalState, setPdfModalState] = useState<PdfModalsState>(
    PdfModalsState.None
  );
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const isOnReportView = location.pathname.includes('/report/');
  const isOnChatBvView = location.pathname.includes('/chatbv');
  const isOnDeepSearchView = location.pathname.includes('/deepsearch');
  // TODO
  // const isOnChatBvChatPage = location.pathname.includes('/chatbv/chat');
  const wrapperId = isUserInTest && isDesktop ? 'app-container' : undefined;

  const deepSearchStyles = isOnDeepSearchView
    ? getDeepSearchWrapperStyles(theme)
    : {};
  // END DERIVED_DATA_AND_UTILS

  // START EFFECTS
  useEffect(() => {
    if (account?.account?.user_info?.password_delayed) {
      window.open(
        'https://www.beenverified.com/account/password_update',
        '_self'
      );
    }
  }, [account?.account?.user_info?.password_delayed]);

  useEffect(() => {
    if (showFirstTimePDFModal) {
      setPdfModalState(PdfModalsState.AddonCheckoutModal);
    }
  }, [showFirstTimePDFModal]);
  // END EFFECTS

  // START RENDER
  return (
    <AppWrapper
      id={wrapperId}
      sx={{
        marginX: isOnReportView ? 0 : null,
        paddingBottom: isOnChatBvView ? '0' : '2px',
        height: isOnChatBvView ? '100vh' : 'unset',
        backgroundColor:
          isOnChatBvView && isMobileChatBreakpoint
            ? theme.palette.background.paper
            : theme.palette.background.default,
        [theme.breakpoints.up('md')]: {
          marginTop: isOnChatBvView ? '0' : '55px',
        },
        ...deepSearchStyles,
      }}
      isOnReportView={isOnReportView}
      isOnChatBvView={isOnChatBvView}
      isDesktop={isDesktop}
    >
      <Box
        sx={{
          ...(isOnDeepSearchView && deepSearchContentStyles),
        }}
      >
        {children}
      </Box>
      <PdfUpgradeBrandWrapper
        modalState={pdfModalState}
        setModalState={setPdfModalState}
      />
    </AppWrapper>
  );
  // END RENDER
}
